import { Dropdown, Button, Tabs } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";
import { HiUser, HiMail, HiPhone, HiLocationMarker, HiCalendar, HiClock, HiPencil, HiTrash, HiPlus, HiX, HiCheck, HiSearch } from "react-icons/hi";

type ContentType = {
    lastName: string;
    name: string;
    email: string;
    phone: string;
    birthdate: string;
    address: string;
    is_on_time: boolean;
    entry_date: string;
    total: number;
    active: string;
    seniority: number;
}

type Client = {
    id: number;
    lastName: string;
    name: string;
    email: string;
    phone: string;
    birthdate: string;
    address: string;
    is_on_time: boolean;
    entry_date: string;
    total: string;
    active: string;
    seniority: string;
}

export default function ClientList() {

    type TransactionType = {
        id: number;
        product: string;
        price: string;
        date: string;
    }

    type StockType = {
        name: string;
        price: string;
        stock: string;
    }

    const [paymentAmount, setPaymentAmount] = useState<number>(0);
    const [clientList, setClientList] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [transaction, setTransaction] = useState<TransactionType[]>([]);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openModalInfos, setOpenModalInfos] = useState<boolean>(false);
    const [client, setClient] = useState<Client>({
        id: 0,
        lastName: '',
        name: '',
        email: '',
        phone: '',
        birthdate: '',
        address: '',
        is_on_time: true,
        entry_date: '',
        active: '',
        total: '',
        seniority: '',
    })
    const [content, setContent] = useState<ContentType>({
        lastName: '',
        name: '',
        email: '',
        phone: '',
        birthdate: '',
        address: '',
        is_on_time: true,
        entry_date: '',
        active: '',
        total: 150,
        seniority: 0,
    });
    const [stock, setStock] = useState<StockType[]>([]);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [clientToEdit, setClientToEdit] = useState<Client | null>(null);

    // Filter clients based on search term
    useEffect(() => {
        if (searchTerm === '') {
            setFilteredClients(clientList);
        } else {
            const filtered = clientList.filter((c: Client) =>
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.lastName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredClients(filtered);
        }
    }, [searchTerm, clientList]);

    const makeRequest = useCallback((client: Client) => {
        setClient(client);
        fetch(`${process.env.REACT_APP_BACK_URL}/transaction?name=${client?.name}&lastname=${client?.lastName}&year=${new Date().getFullYear()}`)
            .then(response => response.json())
            .then(data => { setTransaction(data); console.log(data) })
            .then(() => console.log("transaction", transaction))
            .catch(error => console.error(error))
    }, []);

    const fetchDatas = async () => {
        await fetch(`${process.env.REACT_APP_BACK_URL}/client`)
            .then(response => response.json())
            .then(data => {
                return data.sort((a: Client, b: Client) => a.lastName.localeCompare(b.lastName));
            })
            .then(data => {
                const newClient: Client = data.find((item: Client) => {
                    return item.name === client.name && item.lastName === client.lastName
                }) || { id: 0, lastName: '', name: '', active: '', total: '' };
                setClient(newClient);
                setClientList(data)
            })
            .catch(error => console.error(error));

        await fetch(`${process.env.REACT_APP_BACK_URL}/stock`)
            .then(response => response.json())
            .then(data => setStock(data))
            .catch(error => console.error(error));
    }

    const addTransactions = async (args: string) => {
        const stockIndex = stock.findIndex(item => item.name === args);
        fetch(`${process.env.REACT_APP_BACK_URL}/transaction`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: client?.name,
                lastName: client?.lastName,
                product: args,
                price: stock[stockIndex].price,
                date: new Date(),
                year: new Date().getFullYear()
            })
        })
            .then(response => {
                fetchDatas();
                makeRequest(client);
            })
            .catch(error => console.error(error));
    }

    const addPayment = async () => {
        fetch(`${process.env.REACT_APP_BACK_URL}/transaction`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: client?.name,
                lastName: client?.lastName,
                product: "Remboursement",
                price: paymentAmount,
                date: new Date(),
                year: new Date().getFullYear()
            })
        })
            .then(response => {
                fetchDatas();
                makeRequest(client);
            })
            .catch(error => console.error(error));
    }

    useEffect(() => {
        fetchDatas();
    }, []);

    const deleteTransaction = useCallback((id: Number) => {
        fetch(`${process.env.REACT_APP_BACK_URL}/transaction`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: id,
                email: client.email
            })
        })
            .then(response => {
                fetchDatas();
                makeRequest(client);
            })
            .catch(error => console.error(error));
    }, [client]);

    const changeYear = useCallback((tabIndex: number) => {
        // Map tab index to year (0 = 2025, 1 = 2026, 2 = 2027)
        const years = [2025, 2026, 2027];
        const year = years[tabIndex] || new Date().getFullYear();
        fetch(`${process.env.REACT_APP_BACK_URL}/transaction?name=${client?.name}&lastname=${client?.lastName}&year=${year}`)
            .then(response => response.json())
            .then(data => setTransaction(data))
            .catch(error => console.error(error));
    }, [client]);

    return (
        <>
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h1 className="page-title">Liste des Amicalistes</h1>
                
                {/* Search Bar */}
                <div className="relative w-full sm:w-80">
                    <HiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Rechercher un membre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg shadow-indigo-200">
                    <p className="text-indigo-100 text-sm font-medium">Total Membres</p>
                    <p className="text-3xl font-bold mt-1">{clientList.length}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white shadow-lg shadow-emerald-200">
                    <p className="text-emerald-100 text-sm font-medium">Membres Actifs</p>
                    <p className="text-3xl font-bold mt-1">{clientList.filter((c: Client) => c.active === 'Oui').length}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-200">
                    <p className="text-amber-100 text-sm font-medium">À Jour</p>
                    <p className="text-3xl font-bold mt-1">{clientList.filter((c: Client) => c.is_on_time).length}</p>
                </div>
                <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-5 text-white shadow-lg shadow-rose-200">
                    <p className="text-rose-100 text-sm font-medium">Total Quote-Part</p>
                    <p className="text-3xl font-bold mt-1">{clientList.reduce((acc: number, c: Client) => acc + Number(c.total), 0).toFixed(2)} €</p>
                </div>
            </div>

            {/* Table Card */}
            <div className="card">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Nom</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Prénom</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Solde</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredClients && filteredClients.map((clientItem: Client, index: number) => (
                                <tr 
                                    key={index} 
                                    className="hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                                    onClick={() => { setOpenModalInfos(true); makeRequest(clientItem) }}
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                                                {clientItem.lastName.charAt(0)}{clientItem.name.charAt(0)}
                                            </div>
                                            <span className="font-semibold text-slate-800">{clientItem.lastName}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">{clientItem.name}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                                            clientItem.active === 'Oui' 
                                                ? 'bg-emerald-100 text-emerald-700' 
                                                : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${
                                                clientItem.active === 'Oui' ? 'bg-emerald-500' : 'bg-slate-400'
                                            }`}></span>
                                            {clientItem.active === 'Oui' ? 'Actif' : 'Inactif'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`font-semibold ${
                                            Number(clientItem.total) < 0 ? 'text-rose-600' : 'text-emerald-600'
                                        }`}>
                                            {clientItem.total} €
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); setEditMode(true); setClientToEdit(clientItem); }}
                                                className="p-2 rounded-lg bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
                                            >
                                                <HiPencil className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
                                                        fetch(`${process.env.REACT_APP_BACK_URL}/client`, { 
                                                            method: 'DELETE', 
                                                            headers: { 'Content-Type': 'application/json' }, 
                                                            body: JSON.stringify({ email: clientItem.email }) 
                                                        })
                                                        .then(response => fetchDatas())
                                                        .catch(error => console.error(error)); 
                                                    }
                                                }}
                                                className="p-2 rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors"
                                            >
                                                <HiTrash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {/* Add Button */}
                <div className="p-4 border-t border-slate-100">
                    <button 
                        onClick={() => setOpenModal(true)} 
                        className="btn-primary flex items-center gap-2"
                    >
                        <HiPlus className="w-5 h-5" />
                        Ajouter un membre
                    </button>
                </div>
            </div>
            {/* Add Client Modal */}
            {openModal && (
                <AddClient content={content} setContent={setContent} setOpenModal={setOpenModal} fetchDatas={fetchDatas} />
            )}

            {/* Client Details Modal */}
            {openModalInfos && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setOpenModalInfos(false)}
                    ></div>
                    
                    {/* Modal Content */}
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto animate-scaleIn">
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                                    {client.lastName.charAt(0)}{client.name.charAt(0)}
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800">{client.name} {client.lastName}</h2>
                                    <p className="text-sm text-slate-500">Fiche membre</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setOpenModalInfos(false)}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                            >
                                <HiX className="w-6 h-6 text-slate-500" />
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Info Cards Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                {/* Contact Info */}
                                <div className="bg-slate-50 rounded-2xl p-5">
                                    <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <HiUser className="w-5 h-5 text-indigo-500" />
                                        Informations de contact
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-sm">
                                            <HiMail className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{client.email || 'Non renseigné'}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm">
                                            <HiPhone className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{client.phone || 'Non renseigné'}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm">
                                            <HiLocationMarker className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{client.address || 'Non renseigné'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Membership Info */}
                                <div className="bg-slate-50 rounded-2xl p-5">
                                    <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <HiCalendar className="w-5 h-5 text-indigo-500" />
                                        Adhésion
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-500">Statut</span>
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                                client.active === 'Oui' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                                            }`}>
                                                {client.active === 'Oui' ? 'Actif' : 'Inactif'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-500">Cotisation</span>
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                                client.is_on_time ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                                            }`}>
                                                {client.is_on_time ? 'À jour' : 'En retard'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-500">Ancienneté</span>
                                            <span className="text-sm font-semibold text-slate-700">{client.seniority} ans</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-500">Date d'entrée</span>
                                            <span className="text-sm font-semibold text-slate-700">{client.entry_date}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Transactions Section */}
                            <div className="bg-slate-50 rounded-2xl p-5">
                                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <HiClock className="w-5 h-5 text-indigo-500" />
                                    Historique des transactions
                                </h4>
                                <Tabs aria-label="Tabs années" variant="underline" onActiveTabChange={(tab) => { changeYear(tab) }}>
                                    <Tabs.Item title="2025" />
                                    <Tabs.Item active title="2026" />
                                    <Tabs.Item title="2027" />
                                </Tabs>

                                <div className="mt-4 overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-slate-200">
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Produit</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Montant</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {transaction.length > 0 ? (
                                                transaction.map((trans) => {
                                                    const date = new Date(trans.date);
                                                    const isPayment = trans.product === 'Remboursement';
                                                    return (
                                                        <tr key={trans.id} className={`${isPayment ? 'bg-emerald-50' : 'bg-white'}`}>
                                                            <td className="px-4 py-3">
                                                                <span className={`font-medium ${isPayment ? 'text-emerald-700' : 'text-slate-700'}`}>
                                                                    {trans.product}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`font-semibold ${isPayment ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                                    {isPayment ? '+' : '-'}{trans.price} €
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-slate-500">
                                                                {date.toLocaleDateString('fr-FR')} à {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <button
                                                                    onClick={() => deleteTransaction(trans.id)}
                                                                    className="text-rose-500 hover:text-rose-700 text-sm font-medium hover:underline"
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                                                        Aucune transaction pour cette période
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="mt-6 flex flex-wrap gap-4 items-center">
                                <Dropdown 
                                    label="Ajouter un achat" 
                                    dismissOnClick={false}
                                    className="bg-white shadow-xl rounded-xl border-0"
                                >
                                    {stock && stock.map((_stock, index) => (
                                        <Dropdown.Item 
                                            onClick={() => addTransactions(_stock.name)} 
                                            key={index}
                                            className="hover:bg-indigo-50"
                                        >
                                            {_stock.name} - {_stock.price}€
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown>
                                
                                <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-2">
                                    <input 
                                        type="number"
                                        className="w-24 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-indigo-500 outline-none transition-colors"
                                        placeholder="0"
                                        onChange={(e: any) => setPaymentAmount(e.target.value)}
                                    />
                                    <span className="text-slate-500 font-medium">€</span>
                                    <Button 
                                        onClick={() => addPayment()} 
                                        disabled={paymentAmount === 0}
                                        className="bg-gradient-to-r from-emerald-500 to-teal-500 border-0"
                                    >
                                        <HiCheck className="w-4 h-4 mr-1" />
                                        Encaisser
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                {editMode && (
                    <EditMode clientToEdit={clientToEdit} setClientToEdit={setClientToEdit} setEditMode={setEditMode} fetchDatas={fetchDatas} />
                )}
            {/* </div> */}
        </>
    );
}

const AddClient = ({ content, setContent, setOpenModal, fetchDatas }: { content: ContentType, setContent: (e: ContentType) => void, setOpenModal: (openModal: boolean) => void, fetchDatas: any }) => {

    const sendDatas = useCallback(() => {
        console.log(content);
        fetch(`${process.env.REACT_APP_BACK_URL}/client`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(content)
        })
            .then(response => {
                fetchDatas();
                setOpenModal(false);
            })
            .catch(error => console.error(error));
    }, [content]);


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={() => setOpenModal(false)}
            ></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto animate-scaleIn">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex items-center justify-between rounded-t-2xl z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <HiPlus className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">
                            Nouveau membre
                        </h3>
                    </div>
                    <button 
                        type="button" 
                        className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => setOpenModal(false)}
                    >
                        <HiX className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Nom & Prénom */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Prénom</label>
                            <input 
                                type="text" 
                                className="input-modern" 
                                placeholder="Pierre" 
                                onChange={(e) => setContent({ ...content, name: e.target.value })} 
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Nom</label>
                            <input 
                                type="text" 
                                className="input-modern" 
                                placeholder="Dupont" 
                                onChange={(e) => setContent({ ...content, lastName: e.target.value })} 
                            />
                        </div>
                    </div>

                    {/* Email & Téléphone */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">E-mail</label>
                            <input 
                                type="email" 
                                className="input-modern" 
                                placeholder="mail@domain.com" 
                                onChange={(e) => setContent({ ...content, email: e.target.value })} 
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Téléphone</label>
                            <input 
                                type="tel" 
                                className="input-modern" 
                                placeholder="06 12 34 56 78" 
                                onChange={(e) => setContent({ ...content, phone: e.target.value })} 
                            />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Date de naissance</label>
                            <input 
                                type="text" 
                                className="input-modern" 
                                placeholder="01/01/1990" 
                                onChange={(e) => setContent({ ...content, birthdate: e.target.value })} 
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Date d'entrée</label>
                            <input 
                                type="text" 
                                className="input-modern" 
                                placeholder="31/03/2021" 
                                onChange={(e) => setContent({ ...content, entry_date: e.target.value })} 
                            />
                        </div>
                    </div>

                    {/* Ancienneté */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Ancienneté (années)</label>
                            <input 
                                type="number" 
                                className="input-modern" 
                                placeholder="1" 
                                onChange={(e) => setContent({ ...content, seniority: Number(e.target.value) })} 
                            />
                        </div>
                    </div>

                    {/* Adresse */}
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Adresse</label>
                        <input 
                            type="text" 
                            className="input-modern" 
                            placeholder="5 rue de la Paix, 75001 Paris" 
                            onChange={(e) => setContent({ ...content, address: e.target.value })} 
                        />
                    </div>

                    {/* Statut & Cotisation */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-slate-50 rounded-xl p-4">
                            <label className="block mb-3 text-sm font-semibold text-slate-700">Membre actif</label>
                            <div className="flex gap-4">
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        content.active === "Oui" 
                                            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setContent({ ...content, active: "Oui", total: 150 })}
                                >
                                    <input type="radio" className="hidden" checked={content.active === "Oui"} readOnly />
                                    <HiCheck className={`w-4 h-4 ${content.active === "Oui" ? 'opacity-100' : 'opacity-0'}`} />
                                    Oui
                                </label>
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        content.active === "Non" 
                                            ? 'bg-slate-200 text-slate-700 ring-2 ring-slate-400' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setContent({ ...content, active: "Non", total: 0 })}
                                >
                                    <input type="radio" className="hidden" checked={content.active === "Non"} readOnly />
                                    Non
                                </label>
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4">
                            <label className="block mb-3 text-sm font-semibold text-slate-700">Cotisation à jour</label>
                            <div className="flex gap-4">
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        content.is_on_time === true 
                                            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setContent({ ...content, is_on_time: true })}
                                >
                                    <input type="radio" className="hidden" checked={content.is_on_time === true} readOnly />
                                    <HiCheck className={`w-4 h-4 ${content.is_on_time === true ? 'opacity-100' : 'opacity-0'}`} />
                                    Oui
                                </label>
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        content.is_on_time === false 
                                            ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setContent({ ...content, is_on_time: false })}
                                >
                                    <input type="radio" className="hidden" checked={content.is_on_time === false} readOnly />
                                    Non
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Montant */}
                    <div className="bg-indigo-50 rounded-xl p-4">
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Montant de la cotisation</label>
                        <div className="flex items-center gap-2">
                            <input 
                                value={content.total} 
                                onChange={(e) => setContent({ ...content, total: Number(e.target.value) })} 
                                type="number" 
                                className="input-modern w-32" 
                            />
                            <span className="text-slate-600 font-semibold">€</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t border-slate-200">
                    <button 
                        className="btn-secondary"
                        onClick={() => setOpenModal(false)}
                    >
                        Annuler
                    </button>
                    <button 
                        className="btn-primary" 
                        onClick={sendDatas} 
                        disabled={content.name === '' || content.lastName === '' || content.active === ''}
                    >
                        <HiCheck className="w-4 h-4 mr-2 inline" />
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}


const EditMode = ({ clientToEdit, setClientToEdit, setEditMode, fetchDatas }: { clientToEdit: Client | null, setClientToEdit: (client: Client | null) => void, setEditMode: (editMode: boolean) => void, fetchDatas: any }) => {

    const handleUpdateClient = async () => {
        if (!clientToEdit) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_BACK_URL}/client`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(clientToEdit),
            });

            if (response.ok) {
                fetchDatas();
                setEditMode(false);
                setClientToEdit(null);
            } else {
                console.error("Failed to update client:", response.statusText);
            }
        } catch (error) {
            console.error("Error updating client:", error);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={() => { setEditMode(false); setClientToEdit(null); }}
            ></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto animate-scaleIn">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 flex items-center justify-between rounded-t-2xl z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <HiPencil className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">
                            Modifier le membre
                        </h3>
                    </div>
                    <button 
                        type="button" 
                        className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => { setEditMode(false); setClientToEdit(null); }}
                    >
                        <HiX className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Nom & Prénom */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Prénom</label>
                            <input 
                                value={clientToEdit?.name || ''}
                                type="text" 
                                className="input-modern" 
                                placeholder="Pierre" 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, name: e.target.value } as Client)} 
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Nom</label>
                            <input 
                                value={clientToEdit?.lastName || ''}
                                type="text" 
                                className="input-modern" 
                                placeholder="Dupont" 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, lastName: e.target.value } as Client)} 
                            />
                        </div>
                    </div>

                    {/* Téléphone */}
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Téléphone</label>
                        <input 
                            value={clientToEdit?.phone || ''}
                            type="tel" 
                            className="input-modern" 
                            placeholder="06 12 34 56 78" 
                            onChange={(e) => setClientToEdit({ ...clientToEdit, phone: e.target.value } as Client)} 
                        />
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Date de naissance</label>
                            <input 
                                value={clientToEdit?.birthdate || ''}
                                type="text" 
                                className="input-modern" 
                                placeholder="01/01/1990" 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, birthdate: e.target.value } as Client)} 
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Date d'entrée</label>
                            <input 
                                value={clientToEdit?.entry_date || ''}
                                type="text" 
                                className="input-modern" 
                                placeholder="31/03/2021" 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, entry_date: e.target.value } as Client)} 
                            />
                        </div>
                    </div>

                    {/* Ancienneté */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-2 text-sm font-semibold text-slate-700">Ancienneté (années)</label>
                            <input 
                                value={clientToEdit?.seniority || ''}
                                type="number" 
                                className="input-modern" 
                                placeholder="1" 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, seniority: e.target.value } as Client)} 
                            />
                        </div>
                    </div>

                    {/* Adresse */}
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Adresse</label>
                        <input 
                            value={clientToEdit?.address || ''}
                            type="text" 
                            className="input-modern" 
                            placeholder="5 rue de la Paix, 75001 Paris" 
                            onChange={(e) => setClientToEdit({ ...clientToEdit, address: e.target.value } as Client)} 
                        />
                    </div>

                    {/* Statut & Cotisation */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-slate-50 rounded-xl p-4">
                            <label className="block mb-3 text-sm font-semibold text-slate-700">Membre actif</label>
                            <div className="flex gap-4">
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        clientToEdit?.active === "Oui" 
                                            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setClientToEdit({ ...clientToEdit, active: "Oui", total: '150' } as Client)}
                                >
                                    <input type="radio" className="hidden" checked={clientToEdit?.active === "Oui"} readOnly />
                                    <HiCheck className={`w-4 h-4 ${clientToEdit?.active === "Oui" ? 'opacity-100' : 'opacity-0'}`} />
                                    Oui
                                </label>
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        clientToEdit?.active === "Non" 
                                            ? 'bg-slate-200 text-slate-700 ring-2 ring-slate-400' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setClientToEdit({ ...clientToEdit, active: "Non", total: '0' } as Client)}
                                >
                                    <input type="radio" className="hidden" checked={clientToEdit?.active === "Non"} readOnly />
                                    Non
                                </label>
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4">
                            <label className="block mb-3 text-sm font-semibold text-slate-700">Cotisation à jour</label>
                            <div className="flex gap-4">
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        clientToEdit?.is_on_time === true 
                                            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setClientToEdit({ ...clientToEdit, is_on_time: true } as Client)}
                                >
                                    <input type="radio" className="hidden" checked={clientToEdit?.is_on_time === true} readOnly />
                                    <HiCheck className={`w-4 h-4 ${clientToEdit?.is_on_time === true ? 'opacity-100' : 'opacity-0'}`} />
                                    Oui
                                </label>
                                <label 
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                                        clientToEdit?.is_on_time === false 
                                            ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-500' 
                                            : 'bg-white text-slate-600 hover:bg-slate-100'
                                    }`}
                                    onClick={() => setClientToEdit({ ...clientToEdit, is_on_time: false } as Client)}
                                >
                                    <input type="radio" className="hidden" checked={clientToEdit?.is_on_time === false} readOnly />
                                    Non
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Montant avec boutons rapides */}
                    <div className="bg-amber-50 rounded-xl p-4">
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Montant de la cotisation</label>
                        <div className="flex items-center gap-3">
                            <input 
                                value={clientToEdit?.total || ''} 
                                onChange={(e) => setClientToEdit({ ...clientToEdit, total: e.target.value } as Client)} 
                                type="number" 
                                className="input-modern w-32" 
                            />
                            <span className="text-slate-600 font-semibold">€</span>
                            <div className="flex gap-2 ml-auto">
                                <button 
                                    type="button"
                                    className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg font-semibold hover:bg-amber-200 transition-colors"
                                    onClick={() => setClientToEdit({ ...clientToEdit, total: String(Number(clientToEdit?.total) + 150) } as Client)}
                                >
                                    + 150 €
                                </button>
                                <button 
                                    type="button"
                                    className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg font-semibold hover:bg-amber-200 transition-colors"
                                    onClick={() => setClientToEdit({ ...clientToEdit, total: String(Number(clientToEdit?.total) + 450) } as Client)}
                                >
                                    + 450 €
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t border-slate-200">
                    <button 
                        className="btn-secondary"
                        onClick={() => { setEditMode(false); setClientToEdit(null); }}
                    >
                        Annuler
                    </button>
                    <button 
                        className="btn-primary" 
                        onClick={handleUpdateClient} 
                        disabled={!clientToEdit?.name || !clientToEdit?.lastName || !clientToEdit?.active}
                    >
                        <HiCheck className="w-4 h-4 mr-2 inline" />
                        Mettre à jour
                    </button>
                </div>
            </div>
        </div>
    );
}